import { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../../context/AuthContext';
import { getCommissionsByRealtor } from '../../../services/commissionService';

const FILTERS = ['전체', '정산확인 대기', '정산완료'];

const TYPE_LABEL = {
  conversion: '전환',
  referral: '추천',
  consulting: '상담',
};

const formatDate = (c) => {
  if (c.createdAt) return c.createdAt;
  const ts = c.created_at;
  if (!ts) return '';
  const d = ts?.toDate?.() || new Date((ts?.seconds || 0) * 1000);
  return d.toISOString().slice(0, 10);
};

const buildCsv = (rows) => {
  const header = ['일시', '유형', '카테고리', '금액', '상태'];
  const lines = [header.join(',')];
  rows.forEach((r) => {
    lines.push([
      formatDate(r),
      TYPE_LABEL[r.type] || r.type || '',
      r.category || '',
      r.amount || 0,
      r.is_settled ? '정산완료' : '정산확인 대기',
    ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','));
  });
  return lines.join('\n');
};

const downloadCsv = (filename, csv) => {
  const bom = '﻿';
  const blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const SettlementCheck = () => {
  const { userData } = useAuth();
  const [activeFilter, setActiveFilter] = useState('전체');
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userData?.uid) {
      setLoading(false);
      return;
    }
    getCommissionsByRealtor(userData.uid)
      .then((rows) => setCommissions(rows || []))
      .catch(() => setCommissions([]))
      .finally(() => setLoading(false));
  }, [userData?.uid]);

  const { pending, settled, filtered } = useMemo(() => {
    const pendingList = commissions.filter((c) => !c.is_settled);
    const settledList = commissions.filter((c) => !!c.is_settled);
    let view = commissions;
    if (activeFilter === '정산확인 대기') view = pendingList;
    else if (activeFilter === '정산완료') view = settledList;
    return { pending: pendingList, settled: settledList, filtered: view };
  }, [commissions, activeFilter]);

  const sumAmount = (list) => list.reduce((s, c) => s + (c.amount || 0), 0);

  const handleExport = () => {
    if (!filtered.length) return;
    const ymd = new Date().toISOString().slice(0, 10);
    downloadCsv(`정산영수증_${ymd}.csv`, buildCsv(filtered));
  };

  return (
    <Container>
      <CardRow>
        <StatCard>
          <CardIcon>⏱</CardIcon>
          <CardLabel>정산확인 대기</CardLabel>
          <CardCount>{pending.length} 건</CardCount>
          <CardAmount>{sumAmount(pending).toLocaleString()}원</CardAmount>
        </StatCard>
        <StatCard>
          <CardIcon>✅</CardIcon>
          <CardLabel>정산완료</CardLabel>
          <CardCount>{settled.length} 건</CardCount>
          <CardAmount>{sumAmount(settled).toLocaleString()}원</CardAmount>
        </StatCard>
      </CardRow>

      <FilterBar>
        <ChipRow>
          {FILTERS.map((f) => (
            <Chip key={f} $active={activeFilter === f} onClick={() => setActiveFilter(f)}>
              {f}
            </Chip>
          ))}
        </ChipRow>
        <ExportBtn type="button" onClick={handleExport} disabled={!filtered.length}>
          영수증 다운로드
        </ExportBtn>
      </FilterBar>

      {loading ? (
        <EmptyBox>
          <EmptyIcon>…</EmptyIcon>
          <EmptyTitle>불러오는 중...</EmptyTitle>
        </EmptyBox>
      ) : filtered.length === 0 ? (
        <>
          <TableHeader>
            <ThCell>일시</ThCell>
            <ThCell>유형</ThCell>
            <ThCell style={{ textAlign: 'right' }}>금액</ThCell>
            <ThCell style={{ textAlign: 'right' }}>상태</ThCell>
          </TableHeader>
          <EmptyBox>
            <EmptyIcon>!</EmptyIcon>
            <EmptyTitle>정산 내역이 없습니다</EmptyTitle>
            <EmptyDesc>정산 관련 내역이 없습니다.</EmptyDesc>
          </EmptyBox>
        </>
      ) : (
        <>
          <TableHeader>
            <ThCell>일시</ThCell>
            <ThCell>유형</ThCell>
            <ThCell style={{ textAlign: 'right' }}>금액</ThCell>
            <ThCell style={{ textAlign: 'right' }}>상태</ThCell>
          </TableHeader>
          {filtered.map((c) => (
            <Row key={c.id}>
              <Cell>{formatDate(c)}</Cell>
              <Cell>{TYPE_LABEL[c.type] || c.type || '-'}</Cell>
              <Cell style={{ textAlign: 'right' }}>{(c.amount || 0).toLocaleString()}원</Cell>
              <Cell style={{ textAlign: 'right' }}>
                <StatusBadge $settled={!!c.is_settled}>
                  {c.is_settled ? '정산완료' : '대기'}
                </StatusBadge>
              </Cell>
            </Row>
          ))}
        </>
      )}
    </Container>
  );
};

export default SettlementCheck;

const Container = styled.div`
  padding-bottom: ${({ theme }) => theme.spacing.lg};
`;

const CardRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const StatCard = styled.div`
  background: ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing.base};
`;

const CardIcon = styled.div`
  font-size: 20px;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const CardLabel = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const CardCount = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
`;

const CardAmount = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  color: ${({ theme }) => theme.colors.primary};
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

const FilterBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const ChipRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  flex-wrap: wrap;
`;

const Chip = styled.button`
  padding: 8px 14px;
  border-radius: 20px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  cursor: pointer;
  background: ${({ $active, theme }) => $active ? theme.colors.primary : '#fff'};
  color: ${({ $active, theme }) => $active ? '#fff' : theme.colors.text};
  border: ${({ $active, theme }) => $active ? 'none' : `1px solid ${theme.colors.border}`};
`;

const ExportBtn = styled.button`
  padding: 8px 14px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.bgWhite};
  color: ${({ theme }) => theme.colors.primary};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  white-space: nowrap;

  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 0.7fr 1fr 0.8fr;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const ThCell = styled.div`
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 0.7fr 1fr 0.8fr;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const Cell = styled.div`
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  background: ${({ $settled }) => ($settled ? '#D1FAE5' : '#FEF3C7')};
  color: ${({ $settled }) => ($settled ? '#065F46' : '#92400E')};
`;

const EmptyBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.xxxl} 0;
`;

const EmptyIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${({ theme }) => theme.radius.full};
  background: ${({ theme }) => theme.colors.borderLight};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.fontSize.lg};
  color: ${({ theme }) => theme.colors.textLight};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const EmptyTitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const EmptyDesc = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;
